/**
 * Hardware Cost & Markup Management — Super Admin Only
 * Full equipment management: costs, markups, inventory tracking, deployment config.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { toast } from 'sonner';
import { displayProcessor, uniqueProcessors } from '../../lib/processorDisplay';
import {
  Plus, Edit, Trash2, Save, Package, DollarSign, Wifi, Shield,
  ChevronDown, ChevronUp, Box, Upload, X, Image as ImageIcon,
  RefreshCw, AlertTriangle, History
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PLATFORMS = ['CLOVER', 'XEROCHARGE', 'SQUARE', 'EXATOUCH', 'INGENICO', 'PAYANYWHERE'];
const CATEGORIES = ['Terminal', 'POS', 'Kiosk', 'Printer', 'Accessories', 'Card Reader', 'Pin Pad'];
const PROCESSORS = uniqueProcessors(['TSYS-msc', 'North', 'FiservCopilot', 'NMI', 'First Data', 'TSYS', 'Elavon', 'EPX']);
const ENCRYPTIONS = ['TDES', 'P2PE', 'Injected Keys', 'None'];
const KEY_STATUSES = ['not_required', 'required', 'completed'];
const SIM_WIFI = ['None', 'SIM', 'WiFi', 'Both'];
const CONDITIONS = ['new', 'refurbished'];

export default function HardwareMarkup() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Restock state
  const [restockDialogOpen, setRestockDialogOpen] = useState(false);
  const [restockItem, setRestockItem] = useState(null);
  const [restockQty, setRestockQty] = useState(20);
  const [restockNotes, setRestockNotes] = useState('');
  const [restocking, setRestocking] = useState(false);
  const [restockLogs, setRestockLogs] = useState([]);
  const [showRestockHistory, setShowRestockHistory] = useState(false);

  function defaultForm() {
    return {
      product_name: '', platform: '', category: 'Terminal', description: '', image_url: '',
      cost_price: '', markup_type: 'percentage', markup_value: '',
      processor_compatibility: [], encryption_requirements: '', key_injection_status: 'not_required',
      sim_wifi_capability: 'None', condition: 'new', quantity: 1, hw_status: 'active'
    };
  }

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.request('/super-admin/hardware-markup');
      setItems(data);
    } catch (err) {
      toast.error('Failed to load hardware items');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const calcSellPrice = () => {
    const cost = parseFloat(form.cost_price) || 0;
    const markup = parseFloat(form.markup_value) || 0;
    return form.markup_type === 'percentage' ? cost + (cost * markup / 100) : cost + markup;
  };

  const handleSave = async () => {
    if (!form.product_name || !form.platform || !form.cost_price) {
      toast.error('Product name, platform, and cost price are required');
      return;
    }
    try {
      const { _pendingFile, ...formData } = form;
      const payload = {
        ...formData,
        cost_price: parseFloat(formData.cost_price),
        markup_value: parseFloat(formData.markup_value) || 0,
        quantity: parseInt(formData.quantity) || 1,
      };
      if (editing) {
        await api.request(`/super-admin/hardware-markup/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('Hardware item updated');
      } else {
        const created = await api.request('/super-admin/hardware-markup', { method: 'POST', body: JSON.stringify(payload) });
        // Upload pending image if selected
        if (_pendingFile && created?.id) {
          await handleImageUpload(_pendingFile, created.id);
        }
        toast.success(`Hardware item created with ${payload.quantity} units in inventory`);
      }
      setDialogOpen(false);
      setForm(defaultForm());
      setEditing(null);
      setImagePreview(null);
      loadItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item and all its inventory?')) return;
    try {
      await api.request(`/super-admin/hardware-markup/${id}`, { method: 'DELETE' });
      toast.success('Item deleted');
      loadItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openRestock = (item) => {
    setRestockItem(item);
    setRestockQty(Math.max(20 - (item.quantity_in_stock || 0), 10));
    setRestockNotes('');
    setRestockDialogOpen(true);
  };

  const handleRestock = async () => {
    if (!restockItem || restockQty < 1) return;
    setRestocking(true);
    try {
      await api.request(`/equipment/restock/${restockItem.id}`, {
        method: 'POST',
        body: JSON.stringify({ quantity: restockQty, notes: restockNotes }),
      });
      toast.success(`Added ${restockQty} units to ${restockItem.product_name}`);
      setRestockDialogOpen(false);
      setRestockItem(null);
      loadItems();
    } catch (err) {
      toast.error(err.message || 'Failed to restock');
    }
    setRestocking(false);
  };

  const loadRestockHistory = async () => {
    try {
      const logs = await api.request('/equipment/restock-logs?limit=30');
      setRestockLogs(logs || []);
      setShowRestockHistory(true);
    } catch {
      toast.error('Failed to load restock history');
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setImagePreview(item.image_url || null);
    setForm({
      product_name: item.product_name || '',
      platform: item.platform || '',
      category: item.category || 'Terminal',
      description: item.description || '',
      image_url: item.image_url || '',
      cost_price: String(item.cost_price || ''),
      markup_type: item.markup_type || 'percentage',
      markup_value: String(item.markup_value || ''),
      processor_compatibility: item.processor_compatibility || [],
      encryption_requirements: item.encryption_requirements || '',
      key_injection_status: item.key_injection_status || 'not_required',
      sim_wifi_capability: item.sim_wifi_capability || 'None',
      condition: item.condition || 'new',
      quantity: item.quantity_total || 1,
      hw_status: item.status || 'active',
    });
    setDialogOpen(true);
  };

  const toggleProcessor = (proc) => {
    setForm(prev => ({
      ...prev,
      processor_compatibility: prev.processor_compatibility.includes(proc)
        ? prev.processor_compatibility.filter(p => p !== proc)
        : [...prev.processor_compatibility, proc]
    }));
  };

  const handleImageUpload = async (file, itemId) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only JPG and PNG images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/super-admin/hardware-markup/${itemId}/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(err.detail || 'Upload failed');
      }
      const updated = await res.json();
      toast.success('Image uploaded successfully');
      setForm(p => ({ ...p, image_url: updated.image_url }));
      setImagePreview(updated.image_url);
      loadItems();
    } catch (err) {
      toast.error(err.message || 'Failed to upload image');
    }
    setUploading(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Show local preview
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
    // If editing existing item, upload immediately
    if (editing?.id) {
      handleImageUpload(file, editing.id);
    } else {
      // Store file for upload after creation
      setForm(p => ({ ...p, _pendingFile: file }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  };

  const filtered = filterPlatform === 'all' ? items : items.filter(i => i.platform === filterPlatform);
  const lowStockItems = filtered.filter(i => (i.quantity_in_stock || 0) < 20);
  const totalCost = filtered.reduce((s, i) => s + ((i.cost_price || 0) * (i.quantity_total || 1)), 0);
  const totalSell = filtered.reduce((s, i) => s + ((i.sell_price || 0) * (i.quantity_total || 1)), 0);
  const totalUnits = filtered.reduce((s, i) => s + (i.quantity_total || 0), 0);
  const totalInStock = filtered.reduce((s, i) => s + (i.quantity_in_stock || 0), 0);

  return (
    <div className="space-y-6" data-testid="hardware-markup-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="hardware-page-title">Hardware Cost & Markup</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage equipment costs, markups, and inventory. Sell prices auto-reflect on the Equipment tab.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadRestockHistory} data-testid="restock-history-btn">
            <History className="h-4 w-4 mr-2" /> Restock Log
          </Button>
          <Button onClick={() => { setForm(defaultForm()); setEditing(null); setDialogOpen(true); }} data-testid="add-hardware-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Hardware
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
        {[
          { label: 'Products', value: filtered.length, color: '' },
          { label: 'Total Units', value: totalUnits, color: '' },
          { label: 'In Stock', value: totalInStock, color: 'text-green-700' },
          { label: 'Low Stock', value: lowStockItems.length, color: lowStockItems.length > 0 ? 'text-orange-600' : 'text-muted-foreground', alert: lowStockItems.length > 0 },
          { label: 'Total Cost', value: `$${totalCost.toLocaleString(undefined, {minimumFractionDigits:2})}`, color: 'text-red-600' },
          { label: 'Total Sell', value: `$${totalSell.toLocaleString(undefined, {minimumFractionDigits:2})}`, color: 'text-green-700' },
        ].map(s => (
          <div key={s.label} className={`bg-card border rounded-xl p-4 ${s.alert ? 'border-orange-300' : ''}`}>
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              {s.alert && <AlertTriangle className="h-3 w-3 text-orange-500" />}
              {s.label}
            </p>
            <p className={`text-xl font-bold mt-0.5 ${s.color}`} data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <Select value={filterPlatform} onValueChange={setFilterPlatform}>
        <SelectTrigger className="w-48" data-testid="filter-platform"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Platforms</SelectItem>
          {PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Product Cards */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-card border rounded-xl">
          <Box className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No hardware items configured.</p>
          <Button onClick={() => { setForm(defaultForm()); setDialogOpen(true); }} className="mt-3" variant="outline">
            <Plus className="h-4 w-4 mr-2" /> Add First Item
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(item => {
            const margin = (item.sell_price || 0) - (item.cost_price || 0);
            const isExpanded = expandedId === item.id;
            return (
              <div key={item.id} className="bg-card border rounded-xl overflow-hidden" data-testid={`hw-card-${item.id}`}>
                <div className="p-4 flex items-center gap-4">
                  {/* Image or placeholder */}
                  <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                    ) : (
                      <Package className="h-7 w-7 text-muted-foreground/50" />
                    )}
                  </div>

                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-sm">{item.product_name}</h3>
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-medium">{item.platform}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{item.category || 'Terminal'}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${item.condition === 'new' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                        {item.condition || 'new'}
                      </span>
                    </div>
                    {item.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.description}</p>}
                  </div>

                  {/* Pricing */}
                  <div className="text-right flex-shrink-0 space-y-0.5">
                    <p className="text-xs text-muted-foreground">Cost: <span className="text-red-600 font-medium">${item.cost_price?.toFixed(2)}</span></p>
                    <p className="text-sm font-bold text-green-700">Sell: ${item.sell_price?.toFixed(2)}</p>
                    <p className="text-[10px] text-blue-600">Margin: ${margin.toFixed(2)}</p>
                  </div>

                  {/* Inventory badges */}
                  <div className="flex-shrink-0 text-center space-y-0.5">
                    <p className="text-xs text-muted-foreground">Stock</p>
                    <div className="flex gap-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${(item.quantity_in_stock || 0) < 20 ? (item.quantity_in_stock || 0) <= 5 ? 'bg-red-50 text-red-700 font-bold' : 'bg-orange-50 text-orange-700 font-bold' : 'bg-green-50 text-green-700'}`} title="In Stock">{item.quantity_in_stock || 0}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded" title="Assigned">{item.quantity_assigned || 0}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded" title="Deployed">{item.quantity_deployed || 0}</span>
                    </div>
                    {(item.quantity_in_stock || 0) < 20 && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <AlertTriangle className="h-3 w-3 text-orange-500" />
                        <span className="text-[9px] text-orange-600 font-medium">Low Stock</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {(item.quantity_in_stock || 0) < 20 && (
                      <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => openRestock(item)} data-testid={`restock-hw-${item.id}`} title="Quick Restock">
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`edit-hw-${item.id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(item.id)} data-testid={`delete-hw-${item.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t bg-muted/20 p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <p className="text-muted-foreground font-medium">Processors</p>
                      <p>{(item.processor_compatibility || []).join(', ') || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium">Encryption</p>
                      <p>{item.encryption_requirements || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium">Key Injection</p>
                      <p className="capitalize">{item.key_injection_status?.replace(/_/g, ' ') || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium">Connectivity</p>
                      <p>{item.sim_wifi_capability || 'N/A'}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); setEditing(null); setForm(defaultForm()); setImagePreview(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Hardware Item' : 'Add Hardware to Inventory'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label>Product Name / Model</Label>
                <Input value={form.product_name} onChange={e => setForm(p => ({ ...p, product_name: e.target.value }))} className="mt-1" placeholder="e.g. Clover Flex" data-testid="hw-name-input" />
              </div>
              <div>
                <Label>Platform</Label>
                <Select value={form.platform} onValueChange={v => setForm(p => ({ ...p, platform: v }))}>
                  <SelectTrigger className="mt-1" data-testid="hw-platform-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Condition</Label>
                <Select value={form.condition} onValueChange={v => setForm(p => ({ ...p, condition: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1" placeholder="Brief description of the equipment..." data-testid="hw-desc-input" />
            </div>
            <div>
              <Label>Product Image (JPG or PNG, max 5MB)</Label>
              <div
                className="mt-1 border-2 border-dashed rounded-xl p-4 text-center cursor-pointer hover:border-primary/40 transition-colors relative"
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => document.getElementById('hw-image-input').click()}
                data-testid="hw-image-upload-zone"
              >
                <input
                  id="hw-image-input"
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="hw-image-file-input"
                />
                {(imagePreview || form.image_url) ? (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview || form.image_url}
                      alt="Preview"
                      className="h-32 w-auto mx-auto rounded-lg object-contain"
                      data-testid="hw-image-preview"
                    />
                    <button
                      type="button"
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImagePreview(null);
                        setForm(p => ({ ...p, image_url: '', _pendingFile: undefined }));
                      }}
                      data-testid="hw-image-remove"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 py-3">
                    {uploading ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
                    ) : (
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground/40" />
                    )}
                    <p className="text-sm text-muted-foreground">
                      {uploading ? 'Uploading...' : 'Click or drag & drop an image'}
                    </p>
                    <p className="text-xs text-muted-foreground/60">JPG or PNG, max 5MB</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-muted/30 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2"><DollarSign className="h-4 w-4 text-primary" /> Pricing</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Cost Price ($)</Label>
                  <Input type="number" step="0.01" value={form.cost_price} onChange={e => setForm(p => ({ ...p, cost_price: e.target.value }))} className="mt-1" data-testid="hw-cost-input" />
                </div>
                <div>
                  <Label>Markup Type</Label>
                  <Select value={form.markup_type} onValueChange={v => setForm(p => ({ ...p, markup_type: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Markup Value</Label>
                  <Input type="number" step="0.01" value={form.markup_value} onChange={e => setForm(p => ({ ...p, markup_value: e.target.value }))} className="mt-1" data-testid="hw-markup-input" />
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm font-medium text-blue-800">Sell Price (shown to agents):</span>
                <span className="text-lg font-bold text-green-700" data-testid="hw-preview-sell">${calcSellPrice().toFixed(2)}</span>
              </div>
            </div>

            {/* Deployment Config */}
            <div className="bg-muted/30 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Deployment Configuration</h3>
              <div>
                <Label>Processor Compatibility</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {PROCESSORS.map(proc => (
                    <label key={proc} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer text-xs transition ${
                      form.processor_compatibility.includes(proc) ? 'border-primary bg-primary/5 font-medium' : 'border-border'
                    }`}>
                      <input type="checkbox" checked={form.processor_compatibility.includes(proc)} onChange={() => toggleProcessor(proc)} className="accent-primary w-3 h-3" />
                      {displayProcessor(proc)}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Encryption</Label>
                  <Select value={form.encryption_requirements || "none_set"} onValueChange={v => setForm(p => ({ ...p, encryption_requirements: v === "none_set" ? "" : v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none_set">Select...</SelectItem>
                      {ENCRYPTIONS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Key Injection</Label>
                  <Select value={form.key_injection_status} onValueChange={v => setForm(p => ({ ...p, key_injection_status: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{KEY_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Connectivity</Label>
                  <Select value={form.sim_wifi_capability} onValueChange={v => setForm(p => ({ ...p, sim_wifi_capability: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{SIM_WIFI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Quantity (only for create) */}
            {!editing && (
              <div>
                <Label>Initial Stock Quantity</Label>
                <Input type="number" min={1} value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))} className="mt-1 w-32" data-testid="hw-qty-input" />
                <p className="text-xs text-muted-foreground mt-1">Number of units to add to inventory</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); setForm(defaultForm()); setImagePreview(null); }}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-hw-btn">
              <Save className="h-4 w-4 mr-2" /> {editing ? 'Update' : 'Create & Add to Inventory'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restock Dialog */}
      <Dialog open={restockDialogOpen} onOpenChange={(open) => { if (!open) { setRestockDialogOpen(false); setRestockItem(null); } }}>
        <DialogContent className="max-w-md" data-testid="restock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-green-600" /> Quick Restock
            </DialogTitle>
          </DialogHeader>
          {restockItem && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  {restockItem.image_url ? (
                    <img src={restockItem.image_url} alt="" className="w-full h-full object-contain p-1" />
                  ) : (
                    <Package className="h-5 w-5 text-muted-foreground/40" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold">{restockItem.product_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {restockItem.platform} - Current stock: <span className={`font-medium ${(restockItem.quantity_in_stock || 0) <= 5 ? 'text-red-600' : 'text-orange-600'}`}>{restockItem.quantity_in_stock || 0}</span>
                  </p>
                </div>
              </div>
              <div>
                <Label>Units to Add</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  {[10, 20, 50, 100].map(q => (
                    <button
                      key={q}
                      onClick={() => setRestockQty(q)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        restockQty === q ? 'bg-green-50 border-green-300 text-green-700' : 'border-border hover:border-green-200'
                      }`}
                      data-testid={`restock-preset-${q}`}
                    >
                      +{q}
                    </button>
                  ))}
                  <Input
                    type="number"
                    min={1}
                    value={restockQty}
                    onChange={e => setRestockQty(parseInt(e.target.value) || 1)}
                    className="w-20"
                    data-testid="restock-qty-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  New stock will be: <span className="font-medium text-green-700">{(restockItem.quantity_in_stock || 0) + restockQty}</span> units
                </p>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Input
                  value={restockNotes}
                  onChange={e => setRestockNotes(e.target.value)}
                  className="mt-1"
                  placeholder="e.g. PO #12345, supplier restock"
                  data-testid="restock-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRestockDialogOpen(false); setRestockItem(null); }}>Cancel</Button>
            <Button
              onClick={handleRestock}
              disabled={restocking || restockQty < 1}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-restock-btn"
            >
              {restocking ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Restocking...</>
              ) : (
                <><Plus className="h-4 w-4 mr-2" /> Add {restockQty} Units</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restock History Dialog */}
      <Dialog open={showRestockHistory} onOpenChange={(open) => { if (!open) setShowRestockHistory(false); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="restock-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" /> Restock History
            </DialogTitle>
          </DialogHeader>
          {restockLogs.length === 0 ? (
            <div className="text-center py-8">
              <History className="h-10 w-10 mx-auto text-muted-foreground/20 mb-2" />
              <p className="text-sm text-muted-foreground">No restock events recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {restockLogs.map(log => (
                <div key={log.id} className="flex items-center gap-3 p-3 bg-muted/20 rounded-lg border" data-testid={`restock-log-${log.id}`}>
                  <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Plus className="h-4 w-4 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{log.product_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {log.platform} - {log.previous_stock} &rarr; {log.new_stock} units
                      {log.notes && <span className="ml-2 text-muted-foreground/70">({log.notes})</span>}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-bold text-green-700">+{log.quantity_added}</span>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(log.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
